/**
 * Live feedback, computed from the regions and the player's marks alone (CLAUDE.md §10): the
 * answer key never reaches this code. A mark is flagged only when no continuation can fix it.
 */
import type { Feedback } from "../types";
import { resizeRows } from "./progress";
import { contradictions, regionCells, type Contradictions } from "./shading";
import { cellId, type Cell, type NorinoriDefinition, type NorinoriProgress } from "./types";

export const REGION_CONTRADICTION = "region_contradiction";
export const CELL_CONTRADICTION = "cell_contradiction";

const ids = (cells: Cell[]) => cells.map(([r, c]) => cellId(r, c));

/** The contradictions of a board, padded or cropped to the definition's size first. */
export function liveContradictions(definition: NorinoriDefinition, rows: string[]): Contradictions {
  return contradictions(definition.regions, resizeRows(rows, definition.rows, definition.cols));
}

function flaggedRegions(definition: NorinoriDefinition, found: Contradictions): Cell[][] {
  const anchors = new Set(found.regions.map(([r, c]) => `${r},${c}`));
  return regionCells(definition.regions).filter((cells) =>
    anchors.has(`${cells[0][0]},${cells[0][1]}`),
  );
}

/** The cells to tint: every cell of a flagged region, and every flagged shaded cell. */
export function flaggedCells(definition: NorinoriDefinition, found: Contradictions): Set<string> {
  const out = new Set<string>();
  for (const cells of flaggedRegions(definition, found))
    for (const [r, c] of cells) out.add(`${r},${c}`);
  for (const [r, c] of found.cells) out.add(`${r},${c}`);
  return out;
}

export function localFeedback(
  definition: NorinoriDefinition,
  progress: NorinoriProgress,
): Feedback[] {
  const found = liveContradictions(definition, progress.rows);
  const feedback: Feedback[] = flaggedRegions(definition, found).map((cells) => ({
    type: "region",
    code: REGION_CONTRADICTION,
    cells: ids(cells),
  }));
  for (const cell of found.cells)
    feedback.push({ type: "cell", code: CELL_CONTRADICTION, cells: ids([cell]) });
  return feedback;
}
